import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  Grid,
  Alert,
  Tooltip,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useSignupWizard } from '../../../contexts/SignupWizardContext';
import { en } from '../../../translations/en';
import { BillingPeriodToggle } from '../../../components/BillingPeriodToggle';
import { featureFlags } from '../../../utils/featureFlags';
import type { BillingPeriod } from '../../../utils/paymentTiers';

interface SignupStep3PlanProps {
  onNext: () => void;
  onBack: () => void;
}

interface PlanOption {
  id: 'viewer' | 'creator' | 'commercial';
  name: string;
  monthlyPrice: number;
  yearlyPrice: number;
  description: string;
  features: string[];
}

/** Format price for display based on billing period */
const formatPlanPrice = (plan: PlanOption, billingPeriod: BillingPeriod): string => {
  if (plan.monthlyPrice === 0) return 'Free';
  if (billingPeriod === 'monthly') return `$${plan.monthlyPrice}/mo`;
  return `$${plan.yearlyPrice}/year`;
};

/** Format billing detail subtitle */
const formatBillingDetail = (plan: PlanOption, billingPeriod: BillingPeriod): string | null => {
  if (plan.monthlyPrice === 0) return null;
  if (billingPeriod === 'yearly') return `$${(plan.yearlyPrice / 12).toFixed(2)}/mo avg`;
  return null;
};

/** Calculate yearly savings percentage for a specific plan */
const getPlanSavingsPercentage = (plan: PlanOption | undefined): number => {
  if (!plan || plan.monthlyPrice === 0) return 0;
  return Math.round(((plan.monthlyPrice * 12 - plan.yearlyPrice) / (plan.monthlyPrice * 12)) * 100);
};

/** Map feature strings to tooltip keys for jargon terms */
const FEATURE_TOOLTIPS: Record<string, string> = {
  [en.signup.paymentProcessing.creatorFeatures.singleCatalog]: 'catalog',
  [en.signup.paymentProcessing.creatorFeatures.delegates]: 'delegate',
  [en.signup.paymentProcessing.creatorFeatures.legacyManagers]: 'legacyManager',
  [en.signup.paymentProcessing.creatorFeatures.assignedId]: 'aliId',
  [en.signup.paymentProcessing.generalCatalogFeatures.multipleCatalogs]: 'multipleCatalogs',
  [en.signup.paymentProcessing.generalCatalogFeatures.delegates]: 'delegate',
  [en.signup.paymentProcessing.generalCatalogFeatures.legacyManagers]: 'legacyManager',
  [en.signup.paymentProcessing.generalCatalogFeatures.assignedId]: 'aliId',
};

const tooltips = en.signup.planSelection.tooltips;

const ALL_PLANS: PlanOption[] = [
  {
    id: 'viewer',
    name: 'Viewer',
    monthlyPrice: 0,
    yearlyPrice: 0,
    description: en.signup.planSelection.expandedDescriptions.viewer,
    features: ['View public catalogs', 'Save bookmarks', 'Basic search'],
  },
  {
    id: 'creator',
    name: 'Creator',
    monthlyPrice: 15,
    yearlyPrice: 150,
    description: en.signup.planSelection.expandedDescriptions.creator,
    features: [
      en.signup.paymentProcessing.creatorFeatures.singleCatalog,
      en.signup.paymentProcessing.creatorFeatures.delegates,
      en.signup.paymentProcessing.creatorFeatures.legacyManagers,
      en.signup.paymentProcessing.creatorFeatures.assignedId,
    ],
  },
  {
    id: 'commercial',
    name: 'General Catalog',
    monthlyPrice: 60,
    yearlyPrice: 650,
    description: en.signup.planSelection.expandedDescriptions.commercial,
    features: [
      en.signup.paymentProcessing.generalCatalogFeatures.multipleCatalogs,
      en.signup.paymentProcessing.generalCatalogFeatures.catalogItemLimit,
      en.signup.paymentProcessing.generalCatalogFeatures.delegates,
      en.signup.paymentProcessing.generalCatalogFeatures.legacyManagers,
      en.signup.paymentProcessing.generalCatalogFeatures.assignedId,
    ],
  },
];

export const SignupStep3Plan: React.FC<SignupStep3PlanProps> = ({
  onNext,
  onBack: _onBack,
}) => {
  const { state, setSelectedTier, setBillingPeriod } = useSignupWizard();

  // Delegate tier: auto-advance past plan selection (tier is pre-assigned via invitation)
  const isDelegateTier = state.selectedTier === 'delegate';
  React.useEffect(() => {
    if (isDelegateTier) {
      onNext();
    }
  }, [isDelegateTier, onNext]);

  const [localError, setLocalError] = React.useState<string | null>(null);
  const [billingPeriod, setLocalBillingPeriod] = React.useState<BillingPeriod>(
    state.billingPeriod || 'monthly'
  );
  const [selectedPlan, setSelectedPlanLocal] = React.useState<'viewer' | 'creator' | 'commercial' | null>(
    isDelegateTier ? null : state.selectedTier
  );

  // Filter plans based on feature flags
  const plans = React.useMemo(() => {
    if (featureFlags.isEnabled('enableGeneralCatalog')) return ALL_PLANS;
    return ALL_PLANS.filter((p) => p.id !== 'commercial');
  }, []);

  const savingsPercentage = React.useMemo(
    () => getPlanSavingsPercentage(plans.find((p) => p.id === selectedPlan)),
    [plans, selectedPlan]
  );

  const handleSelectPlan = async (planId: 'viewer' | 'creator' | 'commercial') => {
    setSelectedPlanLocal(planId);
    setSelectedTier(planId);
  };

  const handleContinue = async () => {
    if (!selectedPlan) {
      setLocalError('Please select a plan to continue.');
      return;
    }

    setLocalError(null);

    // Persist billing period to context so Step 4 can read it
    setBillingPeriod(billingPeriod);

    onNext();
  };

  // Expose submit handler for parent component
  React.useEffect(() => {
    (window as unknown as { __signupStep3Submit: () => Promise<void> }).__signupStep3Submit =
      handleContinue;
    return () => {
      delete (window as unknown as { __signupStep3Submit?: () => Promise<void> })
        .__signupStep3Submit;
    };
  });

  if (isDelegateTier) {
    return null;
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Choose Your Plan
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Select the plan that best fits your needs. You can upgrade or downgrade at any time.
      </Typography>

      <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Help me choose:</strong>{' '}
          {featureFlags.isEnabled('enableGeneralCatalog')
            ? en.signup.planSelection.helpMeChoose
            : en.signup.planSelection.helpMeChooseNoCommercial}
        </Typography>
      </Alert>

      {localError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {localError}
        </Alert>
      )}

      <BillingPeriodToggle
        value={billingPeriod}
        onChange={setLocalBillingPeriod}
        savingsPercentage={savingsPercentage}
      />

      <Grid container spacing={2}>
        {plans.map((plan) => (
          <Grid size={{ xs: 12, sm: 4 }} key={plan.id}>
            <Card
              variant={selectedPlan === plan.id ? 'elevation' : 'outlined'}
              sx={{
                height: '100%',
                position: 'relative',
                border:
                  selectedPlan === plan.id ? '2px solid' : '1px solid',
                borderColor:
                  selectedPlan === plan.id ? 'primary.main' : 'divider',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  borderColor: 'primary.light',
                  boxShadow: 2,
                },
              }}
            >
              <CardActionArea
                onClick={() => handleSelectPlan(plan.id)}
                sx={{ height: '100%' }}
              >
                <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Typography variant="h6" component="div">
                      {plan.name}
                    </Typography>
                    {selectedPlan === plan.id && (
                      <CheckCircleIcon color="primary" />
                    )}
                  </Box>

                  <Typography
                    variant="h5"
                    color="primary"
                    sx={{ my: 1, fontWeight: 'bold' }}
                  >
                    {formatPlanPrice(plan, billingPeriod)}
                  </Typography>

                  {formatBillingDetail(plan, billingPeriod) && (
                    <Typography variant="caption" color="text.secondary">
                      {formatBillingDetail(plan, billingPeriod)}
                    </Typography>
                  )}

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {plan.description}
                  </Typography>

                  <Box sx={{ flexGrow: 1 }}>
                    {plan.features.map((feature, index) => {
                      const tooltipKey = FEATURE_TOOLTIPS[feature] as keyof typeof tooltips | undefined;
                      const tooltipText = tooltipKey ? tooltips[tooltipKey] : null;
                      return (
                        <Typography
                          key={index}
                          variant="body2"
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            mb: 0.5,
                            '&::before': {
                              content: '"•"',
                              mr: 1,
                              color: 'primary.main',
                            },
                          }}
                        >
                          {feature}
                          {tooltipText && (
                            <Tooltip title={tooltipText} arrow>
                              <InfoOutlinedIcon
                                fontSize="small"
                                color="action"
                                sx={{ ml: 0.5, fontSize: '0.875rem', cursor: 'help' }}
                              />
                            </Tooltip>
                          )}
                        </Typography>
                      );
                    })}
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default SignupStep3Plan;
